import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import PaidIcon from '@mui/icons-material/Paid'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import DownloadIcon from '@mui/icons-material/Download'
import API from '../../../../services/API'
import downloadFile from '../../../../helpers/downloadFile'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { vatMultiplierForItem } from '../../../../helpers/region'

const lineItemsTotal = (items, region) =>
  (items || []).reduce((total, item) => {
    const vatMultiplier = vatMultiplierForItem(item.vat, region)
    return (
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplier
    )
  }, 0)

// VAT-inclusive gross, net of credit notes and the flat discount - matches
// the server's netAmount maths
const getInvoiceTotal = (invoice, region) => {
  const credited = (invoice?.creditNotes || []).reduce(
    (total, note) => total + lineItemsTotal(note.items, region),
    0,
  )
  return (
    lineItemsTotal(invoice?.items, region) -
    credited -
    Number(invoice?.discount || 0)
  )
}

// Stored status is authoritative (a scheduler flips invoices to Overdue);
// the Sent/Partially Paid past-due check covers invoices from before
// statuses were stored reliably
const isOverdue = (invoice) =>
  invoice.status === 'Overdue' ||
  (['Sent', 'Partially Paid'].includes(invoice.status) &&
    moment(invoice.dueDate).isBefore(moment(), 'day'))

const sourceMeta = {
  both: { label: 'Both', icon: <ReceiptLongIcon fontSize="small" /> },
  Event: { label: 'Events', icon: <EventIcon fontSize="small" /> },
  PTS: { label: 'PTS', icon: <LocalHospitalIcon fontSize="small" /> },
}

export const SummaryTile = ({ label, value, icon, tone = 'primary' }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
    }}
  >
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
      </Stack>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center',
          bgcolor: `${tone}.light`,
          color: `${tone}.dark`,
        }}
      >
        {icon}
      </Box>
    </Stack>
  </Paper>
)

const Invoices = () => {
  const [loading, setLoading] = useState(true)
  const [invoices, setInvoices] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [source, setSource] = useState('both')
  const [selection, setSelection] = useState([])
  const navigate = useNavigate()
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const { setToast } = useContext(GlobalContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'
  const region = tenantInfo?.region

  const loadInvoices = () => {
    setLoading(true)
    setFetchError(false)
    const query = source === 'both' ? '' : `?source=${source}`
    API.get(`/finance/invoices${query}`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setInvoices(res.data)
        } else {
          setInvoices([])
          setFetchError(res?.data?.error || true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadInvoices()
  }, [source])

  const summary = useMemo(() => {
    return invoices.reduce(
      (acc, invoice) => {
        const total = getInvoiceTotal(invoice, region)
        const paid = Number(invoice.totalPaid || 0)
        const outstanding = Math.max(0, total - paid)
        acc.total += total
        acc.paid += paid
        acc.outstanding += outstanding
        if (isOverdue(invoice)) {
          acc.overdue += outstanding
        }
        return acc
      },
      { total: 0, paid: 0, outstanding: 0, overdue: 0 },
    )
  }, [invoices, region])

  const exportToXero = async () => {
    if (!selection.length) return
    // window.open carries no X-Tenant-Subdomain header, so the API can't
    // resolve the tenant in production - download through axios instead
    const result = await downloadFile(
      `/finance/invoices/xero-export?ids=${selection.join(',')}`,
      `xero-invoices-${moment().format('YYYYMMDD')}.csv`,
    )
    if (!result.ok) {
      setToast?.({ severity: 'error', message: result.error })
    }
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={2}
      >
        <Stack spacing={0.5}>
          <Typography variant="title2">Invoices</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage event and patient transport invoices from one place.
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.5}
          alignItems={{ md: 'center' }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={source}
            onChange={(event, nextSource) =>
              nextSource && setSource(nextSource)
            }
          >
            {Object.entries(sourceMeta).map(([value, meta]) => (
              <ToggleButton key={value} value={value}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  {meta.icon}
                  {meta.label}
                </Stack>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={!selection.length}
            onClick={exportToXero}
          >
            {selection.length
              ? `Export ${selection.length} to Xero (CSV)`
              : 'Select invoices to export'}
          </Button>
        </Stack>
      </Stack>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="Invoice Value"
              value={`${currency}${summary.total.toFixed(2)}`}
              icon={<ReceiptLongIcon />}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="Paid"
              value={`${currency}${summary.paid.toFixed(2)}`}
              icon={<PaidIcon />}
              tone="success"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="Outstanding"
              value={`${currency}${summary.outstanding.toFixed(2)}`}
              icon={<ReceiptLongIcon />}
              tone="warning"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SummaryTile
              label="Overdue"
              value={`${currency}${summary.overdue.toFixed(2)}`}
              icon={<WarningAmberIcon />}
              tone="error"
            />
          </Grid>
        </Grid>
      </Box>
      {fetchError && (
        <Paper
          elevation={0}
          sx={{ p: 2, border: '1px solid', borderColor: 'error.light' }}
        >
          <Typography color="error">
            {typeof fetchError === 'string'
              ? fetchError
              : 'Unable to load invoices.'}
          </Typography>
        </Paper>
      )}

      <CustomDataGrid
        norowsoverlay={
          <Alert severity="info">
            No invoices yet. Invoices are raised from the modules that generate
            the work: event invoices from a client's event (Clients → client
            → Invoices), and PTS invoices from billing runs or individual
            bookings. They all appear here once created.
          </Alert>
        }
        rows={invoices}
        loading={loading}
        getRowId={(row) => row._id}
        checkboxSelection
        onRowSelectionModelChange={(model) => setSelection(model)}
        disableRowSelectionOnClick
        autoHeight
        documentTitle="Invoices"
        onRowClick={({ row }) => navigate('/invoices/' + row._id)}
        columns={[
          { field: 'reference', headerName: 'Reference', minWidth: 140 },
          {
            field: 'source',
            headerName: 'Source',
            minWidth: 120,
            renderCell: ({ row }) => (
              <Chip
                size="small"
                icon={
                  row.source === 'PTS' ? <LocalHospitalIcon /> : <EventIcon />
                }
                label={row.source === 'PTS' ? 'PTS' : 'Event'}
              />
            ),
          },
          {
            field: 'description',
            headerName: 'Linked Record',
            minWidth: 240,
            flex: 1,
            valueGetter: (value, row) =>
              row.source === 'PTS'
                ? row.ptsBooking?.bookingRef || ''
                : row.event?.title || '',
            renderCell: ({ row }) =>
              row.source === 'PTS' ? (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/pts/bookings/${row.ptsBooking?._id}`)
                  }}
                  disabled={!row.ptsBooking?._id}
                >
                  {row.ptsBooking?.bookingRef || 'PTS booking'}
                </Button>
              ) : (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/events/${row.event?._id}`)
                  }}
                  disabled={!row.event?._id}
                >
                  {row.event?.title || 'Event'}
                </Button>
              ),
          },
          {
            field: 'organisation',
            headerName: 'Customer',
            valueGetter: (value, row) =>
              row.organisation?.orgName || row.patientCustomer?.name || '',
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'invoiceDate',
            headerName: 'Date',
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
            minWidth: 120,
          },
          {
            field: 'dueDate',
            headerName: 'Due Date',
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
            minWidth: 120,
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 140,
            renderCell: ({ value }) => (
              <Chip
                size="small"
                label={value || 'Draft'}
                color={
                  value === 'Paid'
                    ? 'success'
                    : value === 'Overdue'
                      ? 'error'
                      : value === 'Draft'
                        ? 'default'
                        : 'primary'
                }
              />
            ),
          },
          {
            field: 'totalAmount',
            headerName: 'Total',
            renderCell: ({ row }) =>
              `${currency}${getInvoiceTotal(row, region).toFixed(2)}`,
            minWidth: 120,
          },
          {
            field: 'totalPaid',
            headerName: 'Paid',
            renderCell: ({ value }) =>
              `${currency}${Number(value || 0).toFixed(2)}`,
            minWidth: 110,
          },
          {
            field: 'remainingBalance',
            headerName: 'Due',
            renderCell: ({ row }) =>
              `${currency}${Math.max(
                0,
                getInvoiceTotal(row, region) - Number(row.totalPaid || 0),
              ).toFixed(2)}`,
            minWidth: 110,
          },
        ]}
      />
    </Stack>
  )
}

export default Invoices
